import {
    Sidebar,
    SidebarFooter,
    SidebarMenu,
    SidebarMenuItem,
    SidebarContent,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarGroupContent,
} from '@/shared/components/ui/sidebar'
import { ToggleCard } from '@/components/custom/ToggelCard'
import { useStEditorProvider } from './StEditorProvider'
import { Button } from '@/shared/components/ui/button'
import { Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { useStEditorStore } from '../stores/st-editor.store'
import { hasMinimalStoryContent, getContentStats } from '../utils/contentUtils'
import { STORY_OUTLINE_MIN_WORDS } from '../config/story-content.config'

const StEditorOptionbar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
    const { featuresEnabled, updateFeatureEnabled } = useStEditorProvider()
    const navigate = useNavigate()
    const { project } = useWorkspace()
    const { storyEntity } = useStEditorStore()

    // Check if story has minimal content for screenplay generation
    const hasMinimalContent = storyEntity?.content
        ? hasMinimalStoryContent(storyEntity.content)
        : false
    const contentStats = storyEntity?.content
        ? getContentStats(storyEntity.content)
        : { wordCount: 0, characterCount: 0, nodeCount: 0 }

    // Generate hover hint based on content status
    const getButtonHint = () => {
        if (!storyEntity) {
            return 'No story loaded'
        }
        if (!hasMinimalContent) {
            const wordsNeeded = Math.max(0, STORY_OUTLINE_MIN_WORDS - contentStats.wordCount)
            return `Add at least ${wordsNeeded} more words to generate screenplay outline (${contentStats.wordCount}/${STORY_OUTLINE_MIN_WORDS} words)`
        }
        return 'Generate screenplay outline from your story'
    }

    const helperText = !storyEntity
        ? `Write at least ${STORY_OUTLINE_MIN_WORDS} words to enable screenplay outline generation.`
        : hasMinimalContent
            ? null
            : `Needs at least ${STORY_OUTLINE_MIN_WORDS} words. Current story length: ${contentStats.wordCount}/${STORY_OUTLINE_MIN_WORDS} words.`

    const handleGenerateScreenplay = () => {
        if (!project?.id || !hasMinimalContent) return
        navigate(`/workspace/${project.id}/screenplay`, {
            state: { openChoiceDialog: true, storyContent: storyEntity?.content },
        })
    }

    return (
        <Sidebar
            collapsible="none"
            className="sticky top-0 hidden justify-end h-svh border-l lg:flex"
            {...props}
        >
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>AI Actions</SidebarGroupLabel>
                    <SidebarGroupContent className="p-2">
                        <Button
                            variant="outline"
                            className={`w-full justify-start gap-2 border-dashed transition-colors ${hasMinimalContent
                                ? 'border-primary/50 hover:border-primary hover:bg-primary/5'
                                : 'border-muted-foreground/30 text-muted-foreground'
                                }`}
                            onClick={handleGenerateScreenplay}
                            disabled={!hasMinimalContent}
                            title={getButtonHint()}
                        >
                            <Sparkles className="h-4 w-4 text-primary" />
                            <span>Generate Screenplay Outline</span>
                        </Button>
                        {helperText && (
                            <p className="mt-2 text-xs leading-5 text-muted-foreground">
                                {helperText}
                            </p>
                        )}
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
            <SidebarFooter>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <ToggleCard
                            id="toggle-node-toolbar"
                            titleEnabled="Inline Toolbar shown"
                            titleDisabled="Show Inline Toolbar"
                            description="Floating tools at end of lines"
                            checked={featuresEnabled.nodeToolbar}
                            onChange={(checked) => {
                                updateFeatureEnabled({ ...featuresEnabled, nodeToolbar: checked })
                            }}
                        />
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    )
}

export default StEditorOptionbar
